import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, pollProductionChat, postProductionChat, type ChatWireMessage } from "./api";
import { SC } from "./studioChrome";

// Private Legend↔Director production chat (LSC-404): a producer-only channel separate
// from the fan chat, so the Director can coordinate the show ("2 min to break",
// "your mic is low") and the Legend can reply ("go to board", "need 2 minutes")
// without leaving the console for a phone/Slack. Same API-relayed long-poll shape as
// the fan chat (ADR 0026); gated server-side to producers of this broadcast.

interface ProdMessage {
  id: number;
  user: string;
  text: string;
  self: boolean;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
// A 401/403 is a permanent authorization failure — stop polling, don't spam (LSC-317).
const permanentAuth = (err: unknown) => err instanceof ApiError && (err.status === 401 || err.status === 403);

/**
 * Subscribe to (and send) the private production chat for a broadcast. `unread`
 * counts incoming messages from the other operator since the panel was last read;
 * `markRead()` clears it (call when the operator is looking at the panel).
 */
export function useProductionChat(broadcastId: string | null, enabled: boolean) {
  const [messages, setMessages] = useState<ProdMessage[]>([]);
  const [unread, setUnread] = useState(0);
  const cursorRef = useRef(0);
  const seenRef = useRef<Set<number>>(new Set());
  const mineRef = useRef<Set<number>>(new Set());

  const append = useCallback((w: ChatWireMessage) => {
    if (seenRef.current.has(w.id)) return; // de-dupe (a post + its poll echo)
    seenRef.current.add(w.id);
    if (w.id > cursorRef.current) cursorRef.current = w.id;
    const self = mineRef.current.has(w.id);
    setMessages((m) => [...m, { id: w.id, user: w.user, text: w.text, self }]);
    if (!self) setUnread((u) => u + 1); // a message from the other operator
  }, []);

  // A new broadcast (or re-entry) is a fresh thread.
  useEffect(() => {
    setMessages([]);
    setUnread(0);
    cursorRef.current = 0;
    seenRef.current = new Set();
    mineRef.current = new Set();
  }, [broadcastId]);

  useEffect(() => {
    if (!enabled || !broadcastId) return;
    let cancelled = false;
    const ctrl = new AbortController();
    (async () => {
      while (!cancelled) {
        try {
          const { messages: msgs } = await pollProductionChat(broadcastId, cursorRef.current, ctrl.signal);
          if (cancelled) break;
          for (const w of msgs) append(w);
          if (msgs.length === 0) await sleep(300);
        } catch (err) {
          if (cancelled || ctrl.signal.aborted || permanentAuth(err)) break;
          await sleep(1500); // transient — back off, keep polling
        }
      }
    })();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [enabled, broadcastId, append]);

  const send = useCallback(
    (text: string) => {
      const t = text.trim();
      if (!t || !broadcastId) return;
      postProductionChat(broadcastId, t)
        .then((w) => {
          mineRef.current.add(w.id);
          append(w);
        })
        .catch(() => undefined);
    },
    [broadcastId, append],
  );

  const markRead = useCallback(() => setUnread(0), []);
  return { messages, send, unread, markRead };
}

/**
 * The production-chat panel for both operator consoles — a message list + an input.
 * `label` names the OTHER role for clarity (the Legend sees "Director", vice-versa).
 * Marks read on focus/interaction so the unread badge clears when the operator looks.
 */
export function ProductionChatPanel({
  messages,
  onSend,
  unread,
  onRead,
  peerLabel,
}: {
  messages: ProdMessage[];
  onSend: (text: string) => void;
  unread: number;
  onRead: () => void;
  peerLabel: string;
}) {
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  // Keep the newest message in view.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length]);

  const submit = () => {
    const t = draft.trim();
    if (!t) return;
    onSend(t);
    setDraft("");
  };

  return (
    <div
      aria-label="Production chat"
      onMouseEnter={onRead}
      style={{ display: "flex", flexDirection: "column", gap: 6, minHeight: 140 }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontFamily: SC.mono, fontSize: 10, letterSpacing: "0.06em", color: SC.dim }}>
          PRODUCTION CHAT
        </span>
        {unread > 0 && (
          <span
            aria-label={`${unread} unread`}
            style={{ fontFamily: SC.mono, fontSize: 9, fontWeight: 700, color: "#141416", background: SC.redSoft, borderRadius: 999, padding: "1px 6px" }}
          >
            {unread} NEW
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 10, color: SC.dim2 }}>↔ {peerLabel}</span>
      </div>
      <div
        ref={listRef}
        onClick={onRead}
        style={{ flex: 1, minHeight: 90, maxHeight: 240, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4, padding: 2 }}
      >
        {messages.length === 0 ? (
          <div style={{ fontSize: 11, color: SC.dim2, padding: 4 }}>No messages yet — coordinate the show here.</div>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              style={{
                alignSelf: m.self ? "flex-end" : "flex-start",
                maxWidth: "88%",
                background: m.self ? SC.blueTint : SC.chipBg,
                border: `1px solid ${m.self ? SC.blueEdge : SC.hair}`,
                borderRadius: 8,
                padding: "4px 8px",
              }}
            >
              {!m.self && <div style={{ fontSize: 9, color: SC.dim, marginBottom: 1 }}>{m.user}</div>}
              <div style={{ fontSize: 12, color: SC.text, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.text}</div>
            </div>
          ))
        )}
      </div>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        style={{ display: "flex", gap: 6 }}
      >
        <input
          aria-label="Message to production"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onFocus={onRead}
          placeholder="Message…"
          style={{
            flex: 1,
            minWidth: 0,
            padding: "6px 8px",
            background: SC.panel2,
            border: `1px solid ${SC.hair}`,
            borderRadius: 8,
            color: SC.text,
            font: "inherit",
            fontSize: 12,
          }}
        />
        <button
          type="submit"
          disabled={!draft.trim()}
          style={{
            padding: "6px 12px",
            background: draft.trim() ? SC.blueTint : SC.chipBg,
            border: `1px solid ${draft.trim() ? SC.blueEdge : SC.hair}`,
            borderRadius: 8,
            color: draft.trim() ? SC.blueHi : SC.dim2,
            fontSize: 12,
            cursor: draft.trim() ? "pointer" : "default",
          }}
        >
          Send
        </button>
      </form>
    </div>
  );
}
